/**
 * A movie's per-country maps — `certifications` ({ GR: "Κ-12", GB: "12A" }) and
 * `releaseDates` ({ GR: "2026-10-30" }) — keyed by ISO 3166-1 alpha-2 code.
 * The backend rejects a key that is not two letters, a rating over 32 chars and
 * a date that is not yyyy-MM-dd (`MovieCountryMaps`), so the editor checks the
 * same rules before it writes.
 */
export type CountryMap = Record<string, string>;

export const MAX_CERTIFICATION_LENGTH = 32;

export function normalizeCountryCode(raw: string): string {
  return raw.trim().toUpperCase();
}

export function isCountryCode(raw: string): boolean {
  return /^[A-Z]{2}$/.test(normalizeCountryCode(raw));
}

export function isIsoDate(raw: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  const parsed = new Date(`${raw}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === raw
  );
}

/** Rows sorted by country code, for display. */
export function sortedEntries(
  map: CountryMap | null | undefined,
): [string, string][] {
  return Object.entries(map ?? {}).sort(([a], [b]) => a.localeCompare(b));
}

/** A new map with `code` set to the trimmed `value` (an empty value removes it). */
export function setCountryEntry(
  map: CountryMap | null | undefined,
  code: string,
  value: string,
): CountryMap {
  const next: CountryMap = { ...(map ?? {}) };
  const key = normalizeCountryCode(code);
  const text = value.trim();
  if (text) next[key] = text;
  else delete next[key];
  return next;
}

export function removeCountryEntry(
  map: CountryMap | null | undefined,
  code: string,
): CountryMap {
  const next: CountryMap = { ...(map ?? {}) };
  delete next[code];
  return next;
}

export type CountryMapKind = "certification" | "date";

/** Why `code`/`value` cannot be saved, or `undefined` when they can. */
export function entryError(
  kind: CountryMapKind,
  code: string,
  value: string,
): string | undefined {
  if (!isCountryCode(code)) return "Use a 2-letter country code, e.g. GR";
  const text = value.trim();
  if (!text) return "Enter a value";
  if (kind === "date" && !isIsoDate(text)) return "Use a yyyy-MM-dd date";
  if (kind === "certification" && text.length > MAX_CERTIFICATION_LENGTH)
    return `At most ${MAX_CERTIFICATION_LENGTH} characters`;
  return undefined;
}
